/** Leitungen: Werkzeug zum Zeichnen und Infos/Bearbeiten der ausgewählten Leitung bzw. des Anschlusses. */
import { useEffect, useState } from "react";
import { LINE_TYPES, POINT_TYPES, type Line, type Point } from "@hausplaner/model";
import {
  useStore, setTool, setLineOpts, finishLine, cancelDraft, undoDraftPoint, selectLine, updateLine, updatePoint,
  deleteLineSel, lineLength, ceilZ, optHeight,
} from "../state/store";
import { FLOORS } from "../engine/constants";
import { LINE_INFO, POINT_INFO, HEIGHT_PRESETS } from "../engine/lines";

const cm = (v: number) => Math.round(v * 100);
const m2 = (v: number) => v.toFixed(2).replace(".", ",");

export function LinesPanel() {
  const tool = useStore((s) => s.tool);
  const sel = useStore((s) => s.lineSel);
  if (!tool && !sel) return null;
  return (
    <section className="sheet side" id="linepanel" aria-label="Leitungen">
      <div className="sh-head">
        <h2>{tool ? "Leitungen einzeichnen" : "Leitung"}</h2>
        <button className="x" aria-label="Schließen" onClick={() => { if (tool) setTool(null); selectLine(null); }}>×</button>
      </div>
      <div className="sh-body">
        {tool && <DrawTools />}
        {sel && <Selected />}
      </div>
    </section>
  );
}

function DrawTools() {
  const o = useStore((s) => s.lineOpts);
  const draft = useStore((s) => s.draft);
  const floor = useStore((s) => s.floor);
  const mode = useStore((s) => s.mode);
  const f = floor === "all" ? 1 : floor;
  const zNow = optHeight(f);
  const [zIn, setZIn] = useState(String(cm(zNow)));
  useEffect(() => setZIn(String(cm(zNow))), [zNow]);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tg = (e.target as HTMLElement).tagName; if (tg === "INPUT" || tg === "SELECT") return;
      if (e.key === "Enter") { e.preventDefault(); finishLine(); }
      if (e.key === "Escape") cancelDraft();
      if (e.key === "Backspace" && useStore.getState().draft.length) { e.preventDefault(); undoDraftPoint(); }
    };
    addEventListener("keydown", h); return () => removeEventListener("keydown", h);
  }, []);
  const len = draft.length > 1 ? lineLength({ pts: draft }) : 0;
  return (
    <>
      {o.kind === "line" && (
        <div className="draft">
          <p className="meta" id="ln-status">{draft.length ? `${draft.length} Punkt${draft.length > 1 ? "e" : ""}${len ? ` · ${m2(len)} m` : ""}` : "Noch kein Punkt"}</p>
          <div className="row">
            <button className="btn small primary" id="ln-finish" disabled={draft.length < 2} onClick={finishLine}>Fertig</button>
            <button className="btn small" disabled={!draft.length} onClick={undoDraftPoint}>Letzten Punkt weg</button>
            <button className="btn small" disabled={!draft.length} onClick={cancelDraft}>Abbrechen</button>
          </div>
        </div>
      )}
      <div className="seg seg-full" role="group" aria-label="Was einzeichnen">
        <button aria-pressed={o.kind === "line"} onClick={() => setLineOpts({ kind: "line" })}>Leitung</button>
        <button aria-pressed={o.kind === "point"} onClick={() => { cancelDraft(); setLineOpts({ kind: "point", z: POINT_INFO[o.pt].z }); }}>Anschluss</button>
      </div>
      {o.kind === "line" ? (
        <div className="chips" role="group" aria-label="Leitungsart">
          {LINE_TYPES.map((t) => (
            <button key={t} className="chip" aria-pressed={o.t === t} onClick={() => setLineOpts({ t })}>
              <i style={{ background: LINE_INFO[t].color }} />{LINE_INFO[t].name}
            </button>
          ))}
        </div>
      ) : (
        <div className="chips" role="group" aria-label="Anschluss">
          {POINT_TYPES.map((t) => (
            <button key={t} className="chip" aria-pressed={o.pt === t} onClick={() => setLineOpts({ pt: t, z: POINT_INFO[t].z })}>
              <i className="sym" style={{ background: LINE_INFO[POINT_INFO[t].layer].color }}>{POINT_INFO[t].label}</i>{POINT_INFO[t].name}
            </button>
          ))}
        </div>
      )}
      <div className="field" hidden={mode !== "plan"}>
        <span>Höhe über Fußboden ({FLOORS[f].id})</span>
        <div className="row">
          {HEIGHT_PRESETS.map((p) => (
            <button key={p.name} className="btn small" aria-pressed={o.z === p.z} onClick={() => setLineOpts({ z: p.z })}>
              {p.name}<small className="meta">&nbsp;{cm(p.z === "ceil" ? ceilZ(f) : p.z)}</small>
            </button>
          ))}
          <label className="inline-num">cm
            <input id="ln-z" type="number" inputMode="numeric" min={0} max={400} value={zIn} onChange={(e) => setZIn(e.target.value)}
              onBlur={() => { const v = parseFloat(zIn); if (isFinite(v) && v >= 0) setLineOpts({ z: Math.min(4, v / 100) }); else setZIn(String(cm(zNow))); }}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
          </label>
        </div>
      </div>
      <p className="note">
        {mode !== "plan"
          ? (o.kind === "line"
            ? "Punkte direkt auf Wand, Decke oder Boden antippen – z. B. unten an der Wand, dann oben: das wird ein senkrechtes Stück. Kleine Abweichungen werden zu rechten Winkeln (Umschalt: frei). Letzten Punkt nochmal antippen oder Enter beendet."
            : "Stelle an der Wand antippen – die Höhe ergibt sich aus der Stelle.")
          : (o.kind === "line"
            ? "Punkte im Grundriss antippen (auf der gewählten Höhe). Senkrecht hoch/runter: Höhe oben umstellen, dann weiter tippen (im Grundriss als ↕ markiert). Einfacher in Außen oder Rundgang: direkt an der Wand tippen. Letzten Punkt nochmal antippen oder Enter beendet."
            : "Stelle im Grundriss antippen, um den Anschluss auf der gewählten Höhe zu setzen.")}
      </p>
    </>
  );
}

function Selected() {
  const sel = useStore((s) => s.lineSel)!;
  const line = useStore((s) => (sel.kind === "line" ? s.lines.find((l) => l.id === sel.id) : undefined));
  const point = useStore((s) => (sel.kind === "point" ? s.points.find((p) => p.id === sel.id) : undefined));
  const edit = useStore((s) => s.edit);
  if (!line && !point) return null;
  return (
    <div className="selbox" id="ln-sel">
      <h3 className="subh">Ausgewählt</h3>
      {line ? <LineProps l={line} edit={edit} /> : <PointProps p={point!} edit={edit} />}
    </div>
  );
}

function NoteField({ value, disabled, onSave }: { value?: string; disabled: boolean; onSave: (v: string) => void }) {
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  return (
    <label className="field" htmlFor="ln-note"><span>Notiz</span>
      <input id="ln-note" type="text" maxLength={200} placeholder={disabled ? "–" : "z. B. 3×1,5 mm², Sicherung F4"} disabled={disabled} value={v}
        onChange={(e) => setV(e.target.value)} onBlur={() => { if (v !== (value || "")) onSave(v); }}
        onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
    </label>
  );
}

function LineProps({ l, edit }: { l: Line; edit: boolean }) {
  const zs = l.pts.map((p) => p[2]);
  return (
    <>
      <p className="swl big"><i style={{ background: LINE_INFO[l.t].color }} /><b>{LINE_INFO[l.t].name}</b>
        <span className="meta">&nbsp;· {FLOORS[l.f].name} · {m2(lineLength(l))} m · {Math.min(...zs) === Math.max(...zs) ? `${cm(zs[0])} cm hoch` : `${cm(Math.min(...zs))}–${cm(Math.max(...zs))} cm hoch`}</span></p>
      {edit && (
        <div className="row">
          <select aria-label="Leitungsart" value={l.t} onChange={(e) => updateLine(l.id, { t: e.target.value as Line["t"] })}>
            {LINE_TYPES.map((t) => <option key={t} value={t}>{LINE_INFO[t].name}</option>)}
          </select>
          <label className="inline-num">Höhe cm
            <input type="number" defaultValue={cm(zs[0])} key={l.id + zs[0]} min={0} max={400}
              onBlur={(e) => { const v = parseFloat(e.target.value); if (isFinite(v)) updateLine(l.id, { z: v / 100 }); }}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
          </label>
        </div>
      )}
      <NoteField value={l.note} disabled={!edit} onSave={(v) => updateLine(l.id, { note: v || undefined })} />
      {edit && <button className="btn small danger" id="ln-del" onClick={deleteLineSel}>Leitung löschen</button>}
    </>
  );
}

function PointProps({ p, edit }: { p: Point; edit: boolean }) {
  const info = POINT_INFO[p.t];
  return (
    <>
      <p className="swl big"><i className="sym" style={{ background: LINE_INFO[info.layer].color }}>{info.label}</i><b>{info.name}</b>
        <span className="meta">&nbsp;· {FLOORS[p.f].name} · {cm(p.z)} cm hoch</span></p>
      {edit && (
        <div className="row">
          <select aria-label="Anschluss" value={p.t} onChange={(e) => updatePoint(p.id, { t: e.target.value as Point["t"] })}>
            {POINT_TYPES.map((t) => <option key={t} value={t}>{POINT_INFO[t].name}</option>)}
          </select>
          <label className="inline-num">Höhe cm
            <input type="number" defaultValue={cm(p.z)} key={p.id + p.z} min={0} max={400}
              onBlur={(e) => { const v = parseFloat(e.target.value); if (isFinite(v) && v >= 0) updatePoint(p.id, { z: Math.min(4, v / 100) }); }}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
          </label>
        </div>
      )}
      <NoteField value={p.note} disabled={!edit} onSave={(v) => updatePoint(p.id, { note: v || undefined })} />
      {edit && <button className="btn small danger" id="ln-del" onClick={deleteLineSel}>Anschluss löschen</button>}
    </>
  );
}
